import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';

/**
 * The subset of `GET /organizations/:id/feature-flags` the dashboard reads.
 *
 * The endpoint returns more flags than this (pay-for-others, pay-later,
 * signup documents, chat backend); only the recording pair is typed here
 * because that is all the dashboard consumes today — widen this type when a
 * dashboard surface actually needs another flag, rather than mirroring the
 * whole payload speculatively.
 */
export type OrganizationFeatureFlagsDto = {
    // True when the org has a provisioned VenueAgent. The COARSE gate.
    recordingAvailable: boolean;
    // Precise per-court recording capability: the court IDs that actually have
    // a provisioned camera (union across the org's non-revoked VenueAgents).
    // The API sends it ONLY when at least one court is provisioned; ABSENT
    // means "no precise data" → fall back to `recordingAvailable`. See
    // isRecordingAllowed in ../utils/recording-gate.
    recordingCapableCourtIds?: number[];
};

export const GET_ORGANIZATION_FEATURE_FLAGS_QUERY_KEY = (
    organizationId: string,
) => ['organization', organizationId, 'featureFlags'];

/**
 * FENCED OFF — the dashboard cannot reach this endpoint today. See blocked
 * question B1 in `docs/automation/dashboard-recording-gate-status.md`.
 *
 * `GET /organizations/:id/feature-flags` is the ONE organization route the
 * dashboard consumes that is guarded by `AuthGuard` (the MOBILE guard) rather
 * than `AdminAuthGuard` — its docblock says so: "Slim feature-flag getter for
 * mobile". Every sibling this hook was modelled on (`:id/elo-settings`,
 * `:id/access-codes`, `:id/membershipTypes`, …) uses `AdminAuthGuard`. Two
 * independent API-side blockers follow, either of which alone is fatal:
 *
 *  1. `AuthGuard` → `AuthService.verifyToken` resolves the Firebase app from
 *     the token's own `aud`, i.e. `FIREBASE_KEY_<audience>`. A dashboard token's
 *     audience is the ADMIN Firebase project (`mss-prod-admin` in prod,
 *     `padelfzco-admin-staging` in staging, `padelfzco-admin` locally), whose
 *     key lives in `FIREBASE_DASHBOARD_KEY` — there is no `FIREBASE_KEY_*`
 *     entry for it in any environment (`infrastructure/{production,staging}/
 *     main.tf`, `api/.env`). So the lookup throws and the guard answers **401**.
 *  2. Past that, `assertCallerBelongsToOrg` requires the caller to be a tenant
 *     `User` row whose `organization.id` matches — deliberately, to close a
 *     cross-tenant IDOR (`organization-feature-flags-authz.spec.ts`). A
 *     dashboard admin has no such row, so it answers **403**.
 *
 * The 401 is the dangerous one: `apiClient.fetchApi` treats 401 as "refresh and
 * retry", the refresh succeeds (same admin Firebase app), the retry returns the
 * same 401, and the client then calls `signOutCallback()` — which force-logs the
 * operator out of the dashboard. This hook is called at the top of
 * `CreateAdminBookingModal`, which the courts calendar keeps mounted, so the
 * request fires on page load rather than on modal open.
 *
 * While this is `false` the query never runs, `data` stays undefined, and
 * `showRecordingToggle` takes its documented coarse-org fallback — i.e. exactly
 * the org-level behaviour the dashboard shipped before this epic. No sign-out,
 * no per-court gating.
 *
 * RE-ENABLED 2026-09-07. The API side landed: `GET :id/feature-flags` dropped
 * the mobile `AuthGuard` + `assertCallerBelongsToOrg` and now calls
 * `assertTokenCanReadOrganization` (`api/src/libs/auth/any-token.ts`), which
 * accepts a verified dashboard-admin token holding an `OrganizationMember`
 * row for the org while keeping the cross-tenant fence for tenant tokens. The
 * constant is kept (rather than deleted) as the single switch to flip if the
 * route ever regresses to a mobile-only guard — the failure mode is a
 * force-logout on page load, which is worth a named kill switch.
 */
export const DASHBOARD_CAN_CALL_FEATURE_FLAGS = true;

/**
 * Fetches the org's per-tenant feature flags.
 *
 * Keyed by organizationId so switching orgs in the picker never reuses the
 * previous tenant's flags. Deliberately NOT read off the organization DTO:
 * `recordingCapableCourtIds` is volatile (it changes whenever an agent is
 * provisioned or revoked) and belongs to this query, not to the broad org
 * object the picker caches — the same split mobile made.
 *
 * staleTime is 5 min, matching the organisation feature's other reads: camera
 * provisioning changes rarely, and a stale-by-minutes toggle is harmless
 * because the API is authoritative on whether a booking actually records.
 */
export const useGetOrganizationFeatureFlags = (organizationId: string) => {
    const apiClient = useApiClient();

    return useQuery<OrganizationFeatureFlagsDto>({
        queryKey: GET_ORGANIZATION_FEATURE_FLAGS_QUERY_KEY(organizationId),
        queryFn: () =>
            apiClient.fetchApi<OrganizationFeatureFlagsDto, {}>({
                method: 'GET',
                endpoint: `organizations/${organizationId}/feature-flags`,
            }),
        // DASHBOARD_CAN_CALL_FEATURE_FLAGS is the B1 kill switch — see its
        // docblock. Kept wired because a regression to a mobile-only guard on
        // this route does not merely fail the query: it signs the operator out
        // on courts-calendar page load.
        enabled: DASHBOARD_CAN_CALL_FEATURE_FLAGS && !!organizationId,
        staleTime: 5 * 60 * 1000,
    });
};
